// JSON-RPC helper for the JWT-authenticated MCP endpoint. Use from any
// component that wants to call your own MCP tools from the app itself
// (dashboards, settings, etc) — same code path Claude uses, just from
// inside your UI instead of from a chat client.

export interface McpToolResult<T> {
  structuredContent?: T;
  content?: Array<unknown>;
}

const MCP_URL =
  import.meta.env.VITE_MCP_URL ||
  // Falls back to the auth API base — useful when you front /mcp via the
  // same custom domain (the recommended setup).
  `${import.meta.env.VITE_AUTH_API_URL || ""}/mcp`;

export async function callMcp<T>(
  jwt: string,
  method: string,
  params: Record<string, unknown> = {}
): Promise<T> {
  const res = await fetch(MCP_URL, {
    method: "POST",
    headers: {
      authorization: `Bearer ${jwt}`,
      "content-type": "application/json",
    },
    body: JSON.stringify({ jsonrpc: "2.0", id: Date.now(), method, params }),
  });
  const json = await res.json();
  if (json.error) throw new Error(json.error.message || "MCP error");
  return json.result as T;
}

export async function callTool<T>(
  jwt: string,
  name: string,
  args: Record<string, unknown> = {}
): Promise<T> {
  const result = await callMcp<McpToolResult<T>>(jwt, "tools/call", {
    name,
    arguments: args,
  });
  if (!result.structuredContent) {
    throw new Error(`tool ${name} returned no structuredContent`);
  }
  return result.structuredContent;
}
